import type { SlimLLMClassifierResponse } from "models/LLMs";
import type { IOutlineFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import type { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import { Operator } from "models/Rule";
import {
    getModuleScoreBlockInterval,
    groupModuleScores,
} from "components/ApplicationFilters/FiltersAndChips/ModuleScores/moduleScoreLabels";
import type {
    ApplicationFilters,
    ModuleScore,
    PrimaryDriver,
    QuestionResultFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import type Agentv2 from "models/Agentv2";
import {
    SafetyEventId,
    SafetyEventTypeOperator,
    resolveSafetyEventTypeOperator,
    type SafetyEvent,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import {
    OBJECT_FILTER_KEYS,
    isExtendedMetadataObjectFilterKey,
    type ObjectFilterKey,
    type StaticObjectFilterKey,
} from "./objectFilterKeys";

export type FilterSourceState = "notSet" | "singleValue" | "multiValue";
export type FilterMode = "include" | "exclude";
export type FilterConflictRule = "clamp" | "remove";
export type FilterOptionRestriction = {
    type: "allowOnly" | "denyList";
    values: unknown[];
};
export type FilterModeRestriction = {
    allowedModes: FilterMode[];
};
export type FilterRangeRestriction = {
    min?: number;
    max?: number;
};
export type ModuleScoreValueRestriction = {
    licensedModuleId: string;
    operator?: Operator;
    min?: number;
    max?: number;
    /**
     * The source's own conditions on this module. A chart naming the module starts from these and
     * may tighten them, so the restriction has to carry the conditions themselves: a range takes
     * two rows, and min and max cannot say which comparisons drew them.
     */
    conditions: ModuleScore[];
};
export type ModuleScoreRestriction = {
    byModuleId: Record<string, ModuleScoreValueRestriction>;
};

export type FilterComparison = {
    key: ObjectFilterKey;
    sourceState: FilterSourceState; // notSet, singleValue, multiValue
    sourceIsSet: boolean; // state !== notSet
    sourceValue?: unknown;
    sourceMode?: FilterMode; // include, exclude
    candidateValue?: unknown;
    candidateMode?: FilterMode; // include, exclude
    clampedCandidateValue?: unknown;
    clampedCandidateMode?: FilterMode; // include, exclude
    disabledAtObject: boolean; // is the filter disabled at the object level?
    optionRestriction?: FilterOptionRestriction; // {type: allowList | denyList, values: []}
    modeRestriction?: FilterModeRestriction; // { allowedModes: FilterMode[] }
    rangeRestriction?: FilterRangeRestriction;
    moduleScoreRestriction?: ModuleScoreRestriction;
    conflictRule: FilterConflictRule;
    conflict: boolean;
    allowedValues?: unknown[];
    conflictingValues?: unknown[];
    reason?: string;
};

export type FiltersComparisonResult = {
    byKey: Record<string, FilterComparison>;
    conflicts: FilterComparison[];
};

type FiltersLike = ApplicationFilters | ApplicationFiltersInstanceStore;
export type FilterComparisonContext = {
    agents?: Agentv2[];
};
type ExtendedMetadataGroupedValue = {
    contains: boolean;
    values: string[];
};
type NormalizedSafetyEvent = {
    identification: unknown;
    identificationKey: string;
    typeOperator: SafetyEventTypeOperator;
    typeKeys: string[];
    typeValueByKey: Map<string, unknown>;
    acknowledgmentKeys: string[];
    acknowledgmentValueByKey: Map<string, unknown>;
};

const ARRAY_OBJECT_KEY_ID = {
    topics: (entry: any) => `${entry?.id ?? ""}`,
    themes: (entry: any) => `${entry?.id ?? ""}`,
    primaryTopics: (entry: any) => `${entry?.topicNumber ?? ""}`,
    drivers: (entry: PrimaryDriver) => `${entry?.driverIndex ?? ""}`,
    languages: (entry: any) =>
        `${entry?.languageValue ?? entry?.localeId ?? ""}`,
    dataLabels: (entry: any) => `${entry?.id ?? ""}`,
} as const;
const CALL_DURATION_MAX = 200;
const MESSAGE_COUNT_MAX = 200;
const MODULE_SCORE_MIN = 0;
const MODULE_SCORE_MAX = 100;
const SUPPORTED_MODULE_SCORE_OPERATORS = new Set<Operator>([
    Operator.Gt,
    Operator.GtEq,
    Operator.Lt,
    Operator.LtEq,
    Operator.Eq,
]);
/** Rule Metadata Helpers **/

type ModeCompanionKey =
    | "containsTopics"
    | "containsThemes"
    | "containsPrimaryTopics"
    | "containsDrivers"
    | "containsDataLabels";
/**
 * `singleSelect`: source values disable object editing, but matching object values can still coexist.
 * `multiSelect`: source values constrain object values to an allowed subset, with optional mode narrowing.
 * `sourceExclusive`: any source value fully owns the filter, so object values are disabled and always conflict.
 * `custom`: key-specific comparison/clamping logic handles disable and conflict behavior outside the generic paths.
 */
type RuleType =
    | "singleSelect"
    | "multiSelect"
    | "sourceExclusive"
    | "custom";

type FilterRuleMetadata = {
    ruleType: RuleType;
    conflictRule: FilterConflictRule;
    modeCompanionKey?: ModeCompanionKey;
};

const FILTER_RULES: Record<StaticObjectFilterKey, FilterRuleMetadata> = {
    agentIds: { ruleType: "multiSelect", conflictRule: "clamp" },
    agentManagerIds: { ruleType: "multiSelect", conflictRule: "clamp" },
    mediaTypes: { ruleType: "multiSelect", conflictRule: "clamp" },
    callDirections: { ruleType: "multiSelect", conflictRule: "clamp" },
    callDuration: { ruleType: "custom", conflictRule: "remove" },
    messageCount: { ruleType: "custom", conflictRule: "remove" },
    evaluationTypes: { ruleType: "multiSelect", conflictRule: "clamp" },
    hierarchyIds: { ruleType: "multiSelect", conflictRule: "clamp" },
    clientCallId: { ruleType: "singleSelect", conflictRule: "clamp" },
    languages: { ruleType: "multiSelect", conflictRule: "clamp" },
    meta1: { ruleType: "singleSelect", conflictRule: "clamp" },
    meta2: { ruleType: "singleSelect", conflictRule: "clamp" },
    meta3: { ruleType: "singleSelect", conflictRule: "clamp" },
    meta4: { ruleType: "singleSelect", conflictRule: "clamp" },
    meta5: { ruleType: "singleSelect", conflictRule: "clamp" },
    favoriteConversations: {
        ruleType: "singleSelect",
        conflictRule: "remove",
    },
    embeddedTranscriptionQuery: {
        ruleType: "sourceExclusive",
        conflictRule: "remove",
    },
    wordsAndPhrasesSearchString: {
        ruleType: "sourceExclusive",
        conflictRule: "remove",
    },
    eddyEffectStatus: { ruleType: "singleSelect", conflictRule: "remove" },
    beginningSentiment: { ruleType: "singleSelect", conflictRule: "remove" },
    endingSentiment: { ruleType: "singleSelect", conflictRule: "remove" },
    rbcFilterItems: { ruleType: "sourceExclusive", conflictRule: "remove" },
    outlineSegmentFilterItems: {
        ruleType: "sourceExclusive",
        conflictRule: "remove",
    },
    dataLabels: {
        ruleType: "multiSelect",
        conflictRule: "clamp",
        modeCompanionKey: "containsDataLabels",
    },
    topics: {
        ruleType: "multiSelect",
        conflictRule: "clamp",
        modeCompanionKey: "containsTopics",
    },
    themes: {
        ruleType: "multiSelect",
        conflictRule: "clamp",
        modeCompanionKey: "containsThemes",
    },
    primaryTopics: {
        ruleType: "multiSelect",
        conflictRule: "clamp",
        modeCompanionKey: "containsPrimaryTopics",
    },
    drivers: {
        ruleType: "multiSelect",
        conflictRule: "clamp",
        modeCompanionKey: "containsDrivers",
    },
    contactTypes: { ruleType: "multiSelect", conflictRule: "clamp" },
    patientJourneyStages: { ruleType: "multiSelect", conflictRule: "clamp" },
    safetyEvent: { ruleType: "singleSelect", conflictRule: "clamp" },
    hipaaCompliance: { ruleType: "singleSelect", conflictRule: "remove" },
    modelResults: { ruleType: "custom", conflictRule: "clamp" },
    moduleScores: { ruleType: "custom", conflictRule: "clamp" },
    questionResults: { ruleType: "singleSelect", conflictRule: "remove" },
};

/** Resolves rule metadata for a filter key, including dynamic metadata keys. */
function getRuleMetadata(key: ObjectFilterKey): FilterRuleMetadata {
    if (isExtendedMetadataObjectFilterKey(key)) {
        return { ruleType: "multiSelect", conflictRule: "clamp" };
    }

    return FILTER_RULES[key];
}

/** Returns whether conflicts for the key should clamp or remove the object filter. */
export function getConflictRuleForFilterKey(
    key: ObjectFilterKey,
): FilterConflictRule {
    return getRuleMetadata(key).conflictRule;
}

/** Returns the companion include/exclude mode key for filters that support it. */
export function getModeCompanionKeyForFilterKey(
    key: ObjectFilterKey,
): ModeCompanionKey | undefined {
    if (isExtendedMetadataObjectFilterKey(key)) {
        return undefined;
    }

    return getRuleMetadata(key).modeCompanionKey;
}

/** Extended Metadata Helpers **/

/** Extracts the raw metadata key from an `extendedMetadata.*` object filter key. */
function getExtendedMetadataKey(key: ObjectFilterKey): string | undefined {
    if (!isExtendedMetadataObjectFilterKey(key)) return undefined;
    const metadataKey = key.replace("extendedMetadata.", "");
    return metadataKey.length > 0 ? metadataKey : undefined;
}

/** Reads grouped metadata value/mode from a source or candidate filters object. */
function getExtendedMetadataGroupedValue(
    filters: FiltersLike,
    key: ObjectFilterKey,
): ExtendedMetadataGroupedValue | undefined {
    const metadataKey = getExtendedMetadataKey(key);
    if (!metadataKey) return undefined;

    const groupedValue = (filters as any).extendedMetadataGrouped?.[
        metadataKey
    ] as ExtendedMetadataGroupedValue | undefined;

    if (!groupedValue || !Array.isArray(groupedValue.values)) {
        return undefined;
    }

    const nextValues = groupedValue.values
        .map((value) => `${value}`.trim())
        .filter((value) => value.length > 0);

    if (nextValues.length === 0) return undefined;

    return {
        contains: groupedValue.contains !== false,
        values: nextValues,
    };
}

/** Reads string metadata value for non-grouped metadata filters. */
function getExtendedMetadataStringValue(
    filters: FiltersLike,
    key: ObjectFilterKey,
): string | undefined {
    const metadataKey = getExtendedMetadataKey(key);
    if (!metadataKey) return undefined;

    const value = (filters as any).extendedMetadata?.[metadataKey];
    if (typeof value !== "string") return undefined;

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/** RBC Helpers **/

/** Safety Event Helpers **/

/** De-duplicates scalar values by string identity while preserving original values. */
function normalizeSafetyEventScalarValues(value: unknown): {
    keys: string[];
    valueByKey: Map<string, unknown>;
} {
    const valueByKey = new Map<string, unknown>();

    if (!Array.isArray(value)) {
        return {
            keys: [],
            valueByKey,
        };
    }

    value.forEach((entry) => {
        if (entry === undefined || entry === null) return;
        const key = `${entry}`;
        if (!key.length || valueByKey.has(key)) return;
        valueByKey.set(key, entry);
    });

    return {
        keys: Array.from(valueByKey.keys()).sort((left, right) =>
            left.localeCompare(right),
        ),
        valueByKey,
    };
}

/** Normalizes safety-event filters into a stable shape for implication/equality checks. */
function normalizeSafetyEvent(value: unknown): NormalizedSafetyEvent | undefined {
    if (!value || typeof value !== "object") return undefined;

    const identification = (value as any).identification ?? null;

    const typeOperator = resolveSafetyEventTypeOperator(value as SafetyEvent);
    const { keys: typeKeys, valueByKey: typeValueByKey } =
        normalizeSafetyEventScalarValues((value as any).types);
    const {
        keys: acknowledgmentKeys,
        valueByKey: acknowledgmentValueByKey,
    } = normalizeSafetyEventScalarValues((value as any).acknowledgments);
    if (identification === null && typeKeys.length === 0 && acknowledgmentKeys.length === 0) {
        return undefined;
    }

    return {
        identification,
        identificationKey: identification === null ? "none" : `${identification}`,
        typeOperator,
        typeKeys,
        typeValueByKey,
        acknowledgmentKeys,
        acknowledgmentValueByKey,
    };
}

/** Returns true when the safety-event identification corresponds to "Identified". */
function isSafetyEventIdentified(identification: unknown): boolean {
    return `${identification}` === `${SafetyEventId.Identified}`;
}

/** Returns true when an identified safety-event selection is effectively one concrete choice. */
function isSingleValuedIdentifiedSafetyEvent(
    normalizedSafetyEvent: NormalizedSafetyEvent,
): boolean {
    const sourceRequiresAllSelectedTypes =
        normalizedSafetyEvent.typeOperator === SafetyEventTypeOperator.And &&
        normalizedSafetyEvent.typeKeys.length > 0;

    if (sourceRequiresAllSelectedTypes) {
        return normalizedSafetyEvent.acknowledgmentKeys.length === 1;
    }

    return (
        isSafetyEventIdentified(normalizedSafetyEvent.identification) &&
        normalizedSafetyEvent.typeKeys.length === 1 &&
        normalizedSafetyEvent.acknowledgmentKeys.length === 1
    );
}

/** Returns true when the source safety-event types must remain fixed at the object level. */
function doesSourceRequireAllSafetyEventTypes(
    normalizedSafetyEvent: NormalizedSafetyEvent,
): boolean {
    return (
        normalizedSafetyEvent.typeOperator === SafetyEventTypeOperator.And &&
        normalizedSafetyEvent.typeKeys.length > 0
    );
}

/** Compares normalized safety-event filters while ignoring array order. */
function areSafetyEventsEqual(left: unknown, right: unknown): boolean {
    const normalizedLeft = normalizeSafetyEvent(left);
    const normalizedRight = normalizeSafetyEvent(right);

    if (!normalizedLeft && !normalizedRight) return true;
    if (!normalizedLeft || !normalizedRight) return false;
    if (
        normalizedLeft.identificationKey !== normalizedRight.identificationKey ||
        normalizedLeft.typeOperator !== normalizedRight.typeOperator ||
        normalizedLeft.typeKeys.length !== normalizedRight.typeKeys.length ||
        normalizedLeft.acknowledgmentKeys.length !==
            normalizedRight.acknowledgmentKeys.length
    ) {
        return false;
    }

    return (
        normalizedLeft.typeKeys.every(
            (value, index) => value === normalizedRight.typeKeys[index],
        ) &&
        normalizedLeft.acknowledgmentKeys.every(
            (value, index) =>
                value === normalizedRight.acknowledgmentKeys[index],
        )
    );
}

/** Returns whether candidate type semantics are contained by the source selection. */
function getClampedSafetyEventTypes(
    source: NormalizedSafetyEvent,
    candidate: NormalizedSafetyEvent,
): { valid: boolean; values: unknown[] } {
    if (source.typeKeys.length === 0) {
        return {
            valid: true,
            values: candidate.typeKeys.map(
                (value) => candidate.typeValueByKey.get(value)!,
            ),
        };
    }

    if (candidate.typeKeys.length === 0) {
        return {
            valid: false,
            values: [],
        };
    }

    const sourceTypeSet = new Set(source.typeKeys);
    const candidateMatchesSource = candidate.typeKeys.filter((value) =>
        sourceTypeSet.has(value),
    );
    const sourceIsAnd = source.typeOperator === SafetyEventTypeOperator.And;
    const candidateIsAnd =
        candidate.typeOperator === SafetyEventTypeOperator.And;

    if (!sourceIsAnd && !candidateIsAnd) {
        return {
            valid: candidateMatchesSource.length > 0,
            values: candidate.typeKeys
                .filter((value) => sourceTypeSet.has(value))
                .map((value) => candidate.typeValueByKey.get(value)!),
        };
    }

    if (!sourceIsAnd && candidateIsAnd) {
        return {
            valid: candidateMatchesSource.length > 0,
            values: candidate.typeKeys.map(
                (value) => candidate.typeValueByKey.get(value)!,
            ),
        };
    }

    if (sourceIsAnd && candidateIsAnd) {
        return {
            valid: source.typeKeys.every((value) =>
                candidate.typeValueByKey.has(value),
            ),
            values: candidate.typeKeys.map(
                (value) => candidate.typeValueByKey.get(value)!,
            ),
        };
    }

    if (source.typeKeys.length !== 1) {
        return {
            valid: false,
            values: [],
        };
    }

    const requiredValue = source.typeKeys[0];
    const clampedValues = candidate.typeKeys
        .filter((value) => value === requiredValue)
        .map((value) => candidate.typeValueByKey.get(value)!);

    return {
        valid: clampedValues.length === 1,
        values: clampedValues,
    };
}

/** Returns whether candidate acknowledgment semantics are contained by the source selection. */
function getClampedSafetyEventAcknowledgments(
    source: NormalizedSafetyEvent,
    candidate: NormalizedSafetyEvent,
): { valid: boolean; values: unknown[] } {
    if (source.acknowledgmentKeys.length === 0) {
        return {
            valid: true,
            values: candidate.acknowledgmentKeys.map(
                (value) => candidate.acknowledgmentValueByKey.get(value)!,
            ),
        };
    }

    if (candidate.acknowledgmentKeys.length === 0) {
        return {
            valid: false,
            values: [],
        };
    }

    const sourceAcknowledgmentSet = new Set(source.acknowledgmentKeys);
    const clampedValues = candidate.acknowledgmentKeys
        .filter((value) => sourceAcknowledgmentSet.has(value))
        .map((value) => candidate.acknowledgmentValueByKey.get(value)!);

    return {
        valid: clampedValues.length > 0,
        values: clampedValues,
    };
}

/** Clamps a safety-event candidate to the subset of semantics allowed by the source. */
function clampSafetyEventToSource(
    sourceValue: unknown,
    candidateValue: unknown,
): unknown {
    const source = normalizeSafetyEvent(sourceValue);
    const candidate = normalizeSafetyEvent(candidateValue);

    if (!candidate) return candidateValue;
    if (!source) return candidateValue;
    if (source.identification !== null &&
        candidate.identificationKey !== source.identificationKey) {
        return undefined;
    }

    if (source.identification !== null && !isSafetyEventIdentified(source.identification)) {
        return areSafetyEventsEqual(sourceValue, candidateValue)
            ? candidateValue
            : undefined;
    }

    if (doesSourceRequireAllSafetyEventTypes(source)) {
        const clampedAcknowledgments = getClampedSafetyEventAcknowledgments(
            source,
            candidate,
        );
        if (!clampedAcknowledgments.valid) {
            return undefined;
        }

        return {
            ...(candidateValue as Record<string, unknown>),
            identification: (candidateValue as any).identification,
            typeOperator: source.typeOperator,
            types: source.typeKeys.map((value) => source.typeValueByKey.get(value)!),
            acknowledgments: clampedAcknowledgments.values,
        };
    }

    const clampedTypes = getClampedSafetyEventTypes(source, candidate);
    if (!clampedTypes.valid) {
        return undefined;
    }

    const clampedAcknowledgments = getClampedSafetyEventAcknowledgments(
        source,
        candidate,
    );
    if (!clampedAcknowledgments.valid) {
        return undefined;
    }

    return {
        ...(candidateValue as Record<string, unknown>),
        identification: (candidateValue as any).identification,
        typeOperator: (candidateValue as any).typeOperator,
        types: clampedTypes.values,
        acknowledgments: clampedAcknowledgments.values,
    };
}

/** Returns source restriction metadata for safety-event filtering. */
function getSafetyEventRestrictionForSource(
    sourceFilters: FiltersLike,
    sourceState: FilterSourceState,
): FilterOptionRestriction | undefined {
    if (sourceState === "notSet") return undefined;

    const sourceValue = getByKey(sourceFilters, "safetyEvent");
    if (!normalizeSafetyEvent(sourceValue)) return undefined;

    return {
        type: "allowOnly",
        values: [sourceValue],
    };
}

/** Normalizes RBC filter items into a consistently-shaped array. */
function getRbcFilterItems(value: unknown): IRBCFilter[] {
    if (!Array.isArray(value)) return [];

    return (value as IRBCFilter[]).map((entry) => ({
        value: entry?.value !== false,
        classifierIds: Array.isArray(entry?.classifierIds)
            ? entry.classifierIds
                  .map((classifierId) => `${classifierId}`.trim())
                  .filter((classifierId) => classifierId.length > 0)
            : [],
        combinator: entry?.combinator ?? 0,
        filterGroupId: entry?.filterGroupId ?? 0,
    }));
}

/** Returns unique RBC classifier IDs, optionally scoped to include/exclude mode. */
function getRbcClassifierIds(
    value: unknown,
    mode?: FilterMode,
): string[] {
    const ids = new Set<string>();

    getRbcFilterItems(value).forEach((entry) => {
        const entryMode: FilterMode = entry.value === false ? "exclude" : "include";
        if (mode && entryMode !== mode) return;
        entry.classifierIds.forEach((classifierId) => ids.add(classifierId));
    });

    return Array.from(ids);
}

function normalizeRbcFilterItems(value: unknown): IRBCFilter[] {
    return getRbcFilterItems(value).filter(
        (entry) => entry.classifierIds.length > 0,
    );
}

/** Outline Helpers **/

function getOutlineFilterItems(value: unknown): IOutlineFilter[] {
    if (!Array.isArray(value)) return [];

    return (value as IOutlineFilter[]).map((entry) => ({
        value: entry?.value !== false,
        segments: Array.isArray(entry?.segments)
            ? entry.segments
                  .map((segment) => `${segment}`.trim())
                  .filter((segment) => segment.length > 0)
            : [],
        filterGroupId: entry?.filterGroupId ?? 0,
    }));
}

function getOutlineSegmentIds(
    value: unknown,
    mode?: FilterMode,
): string[] {
    const ids = new Set<string>();

    getOutlineFilterItems(value).forEach((entry) => {
        const entryMode: FilterMode = entry.value === false ? "exclude" : "include";
        if (mode && entryMode !== mode) return;
        entry.segments.forEach((segment) => ids.add(segment));
    });

    return Array.from(ids);
}

function normalizeOutlineFilterItems(value: unknown): IOutlineFilter[] {
    return getOutlineFilterItems(value).filter((entry) => entry.segments.length > 0);
}

/** Module Score Helpers **/

/** Converts any unknown module score input into a bounded numeric value. */
function toModuleScoreNumber(value: unknown): number {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return MODULE_SCORE_MIN;
    }
    const clampedValue = Math.max(
        MODULE_SCORE_MIN,
        Math.min(MODULE_SCORE_MAX, value),
    );
    return Math.round((clampedValue + Number.EPSILON) * 100) / 100;
}

/** Accepts only operators supported by module score filtering. */
function toModuleScoreOperator(value: unknown): Operator | undefined {
    if (typeof value !== "number") return undefined;
    if (!SUPPORTED_MODULE_SCORE_OPERATORS.has(value as Operator)) {
        return undefined;
    }
    return value as Operator;
}

/**
 * Normalizes module-score filters, one entry per condition.
 *
 * A module may carry more than one condition, because `>= 80` and `< 90` together are how a single
 * score range is expressed. Entries are therefore not collapsed by module id. Keying them by module
 * used to keep only the last condition, which silently turned KBo's `Between` expansion
 * (`AgentUtils.ts`) into a one-sided filter wherever a chart-level module score filter was clamped.
 *
 * Identical duplicates are kept as well. They are the user's to remove, and dropping them here
 * would leave the filter the chart runs disagreeing with the one its drawer shows.
 */
function normalizeModuleScores(value: unknown): ModuleScore[] {
    if (!Array.isArray(value)) return [];

    return (value as ModuleScore[])
        .map((entry) => ({
            licensedModuleId: `${entry?.licensedModuleId ?? ""}`.trim(),
            operator: toModuleScoreOperator(entry?.operator),
            moduleScore: toModuleScoreNumber(entry?.moduleScore),
        }))
        .filter((entry) => entry.licensedModuleId.length > 0);
}

/**
 * Builds per-module restrictions from source filter state.
 *
 * A module's conditions are ANDed, so the range it allows is the overlap of them: a dashboard set
 * to `> 20` and `< 80` allows 20 through 80 and nothing wider.
 *
 * The locked operator survives only while it is unambiguous. Two conditions describe a range
 * rather than a comparison, and the pair of rows a chart inherits carries that shape instead.
 */
function getModuleScoreRestrictionForSource(
    sourceFilters: FiltersLike,
): ModuleScoreRestriction | undefined {
    const sourceModuleScores = normalizeModuleScores(
        getByKey(sourceFilters, "moduleScores"),
    );
    if (sourceModuleScores.length === 0) return undefined;

    const byModuleId: Record<string, ModuleScoreValueRestriction> = {};

    groupModuleScores(sourceModuleScores).forEach((group) => {
        const conditions = group.conditions.map((row) => row.entry);
        const interval = getModuleScoreBlockInterval(conditions);

        byModuleId[group.licensedModuleId] = {
            licensedModuleId: group.licensedModuleId,
            operator: conditions.length === 1 ? conditions[0].operator : undefined,
            min: interval.min,
            max: interval.max,
            conditions,
        };
    });

    return { byModuleId };
}

/**
 * Normalizes candidate module-score conditions and otherwise leaves them alone.
 *
 * Nothing needs clamping here any more. A chart's conditions are ANDed with its dashboard's when
 * the request is built, in `BaseObjectStore.getMergedFiltersForRequest`, so a chart's block can
 * only narrow the population the dashboard selected however it is written, and a stale persisted
 * one cannot widen a dashboard filter that has since tightened.
 *
 * What the drawer does with the dashboard's conditions is a separate matter: it seeds a block from
 * them and bounds the values, so the filter on screen reads like the one that runs rather than
 * leaving the dashboard's half of it invisible.
 */
function clampModuleScoresToSource(
    _sourceFilters: FiltersLike,
    candidateValue: unknown,
): ModuleScore[] {
    return normalizeModuleScores(candidateValue);
}

/** Compares module-score arrays by normalized module/operator/score identity. */
function areModuleScoresEqual(left: unknown, right: unknown): boolean {
    const normalizeIdentity = (value: unknown) =>
        normalizeModuleScores(value)
            .map(
                (entry) =>
                    `${entry.licensedModuleId}::${entry.operator ?? ""}::${entry.moduleScore}`,
            )
            .sort((a, b) => a.localeCompare(b));

    const leftIdentity = normalizeIdentity(left);
    const rightIdentity = normalizeIdentity(right);
    if (leftIdentity.length !== rightIdentity.length) return false;

    return leftIdentity.every((value, index) => value === rightIdentity[index]);
}

/** Call Duration Helpers **/

/** Converts any unknown duration input into a safe positive number or zero. */
function toCallDurationNumber(value: unknown): number {
    if (typeof value !== "number" || !Number.isFinite(value)) return 0;
    if (value <= 0) return 0;
    return value;
}

/** Normalizes a call-duration tuple and reports whether it is effectively set. */
function getCallDurationRangeFromTuple(value: unknown): {
    min: number;
    max: number;
    isSet: boolean;
} {
    if (!Array.isArray(value)) {
        return { min: 0, max: 0, isSet: false };
    }

    const min = toCallDurationNumber(value[0]);
    const max = toCallDurationNumber(value[1]);
    return {
        min,
        max,
        isSet: min > 0 || max > 0,
    };
}

/** Reads call duration values from filters as `[min, max]` when present. */
function getCallDurationTupleFromFilters(
    filters: FiltersLike,
): [number, number] | undefined {
    const min = toCallDurationNumber((filters as any).minCallDuration);
    const max = toCallDurationNumber((filters as any).maxCallDuration);
    if (min === 0 && max === 0) return undefined;
    return [min, max];
}

/** Builds range restrictions from the source call duration filter state. */
function getCallDurationRestrictionForSource(
    sourceFilters: FiltersLike,
): FilterRangeRestriction | undefined {
    const sourceTuple = getCallDurationTupleFromFilters(sourceFilters);
    const sourceRange = getCallDurationRangeFromTuple(sourceTuple);
    if (!sourceRange.isSet) return undefined;

    const min = sourceRange.min > 0 ? sourceRange.min : undefined;
    const max = sourceRange.max > 0 ? sourceRange.max : undefined;
    return {
        min,
        max,
    };
}

/** Clamps a candidate call-duration tuple to the source-selected range. */
function clampCallDurationToSourceRange(
    sourceFilters: FiltersLike,
    candidateValue: unknown,
): [number, number] | undefined {
    const sourceTuple = getCallDurationTupleFromFilters(sourceFilters);
    const sourceRange = getCallDurationRangeFromTuple(sourceTuple);
    const candidateRange = getCallDurationRangeFromTuple(candidateValue);

    if (!candidateRange.isSet) return undefined;
    if (!sourceRange.isSet) return [candidateRange.min, candidateRange.max];

    const sourceMin = sourceRange.min;
    const sourceMax =
        sourceRange.max > 0 ? sourceRange.max : CALL_DURATION_MAX;
    const candidateMin = candidateRange.min;
    const candidateMax =
        candidateRange.max > 0 ? candidateRange.max : CALL_DURATION_MAX;

    const clampToSourceRange = (value: number): number =>
        Math.min(sourceMax, Math.max(sourceMin, value));

    const clampedMin = clampToSourceRange(candidateMin);
    const clampedMax = clampToSourceRange(candidateMax);

    return [
        Math.min(clampedMin, clampedMax),
        Math.max(clampedMin, clampedMax),
    ];
}

/** Message Count Helpers **/

/** Converts any unknown message-count input into a safe positive number or zero. */
function toMessageCountNumber(value: unknown): number {
    if (typeof value !== "number" || !Number.isFinite(value)) return 0;
    if (value <= 0) return 0;
    return value;
}

/** Normalizes a message-count tuple and reports whether it is effectively set. */
function getMessageCountRangeFromTuple(value: unknown): {
    min: number;
    max: number;
    isSet: boolean;
} {
    if (!Array.isArray(value)) {
        return { min: 0, max: 0, isSet: false };
    }

    const min = toMessageCountNumber(value[0]);
    const max = toMessageCountNumber(value[1]);
    return {
        min,
        max,
        isSet: min > 0 || max > 0,
    };
}

/** Reads message count values from filters as `[min, max]` when present. */
function getMessageCountTupleFromFilters(
    filters: FiltersLike,
): [number, number] | undefined {
    const min = toMessageCountNumber((filters as any).minMessageCount);
    const max = toMessageCountNumber((filters as any).maxMessageCount);
    if (min === 0 && max === 0) return undefined;
    return [min, max];
}

/** Builds range restrictions from the source message count filter state. */
function getMessageCountRestrictionForSource(
    sourceFilters: FiltersLike,
): FilterRangeRestriction | undefined {
    const sourceTuple = getMessageCountTupleFromFilters(sourceFilters);
    const sourceRange = getMessageCountRangeFromTuple(sourceTuple);
    if (!sourceRange.isSet) return undefined;

    const min = sourceRange.min > 0 ? sourceRange.min : undefined;
    const max = sourceRange.max > 0 ? sourceRange.max : undefined;
    return {
        min,
        max,
    };
}

/** Clamps a candidate message-count tuple to the source-selected range. */
function clampMessageCountToSourceRange(
    sourceFilters: FiltersLike,
    candidateValue: unknown,
): [number, number] | undefined {
    const sourceTuple = getMessageCountTupleFromFilters(sourceFilters);
    const sourceRange = getMessageCountRangeFromTuple(sourceTuple);
    const candidateRange = getMessageCountRangeFromTuple(candidateValue);

    if (!candidateRange.isSet) return undefined;
    if (!sourceRange.isSet) return [candidateRange.min, candidateRange.max];

    const sourceMin = sourceRange.min;
    const sourceMax =
        sourceRange.max > 0 ? sourceRange.max : MESSAGE_COUNT_MAX;
    const candidateMin = candidateRange.min;
    const candidateMax =
        candidateRange.max > 0 ? candidateRange.max : MESSAGE_COUNT_MAX;

    const clampToSourceRange = (value: number): number =>
        Math.min(sourceMax, Math.max(sourceMin, value));

    const clampedMin = clampToSourceRange(candidateMin);
    const clampedMax = clampToSourceRange(candidateMax);

    return [
        Math.min(clampedMin, clampedMax),
        Math.max(clampedMin, clampedMax),
    ];
}

/** Mode and Restriction Helpers **/

/** Resolves include/exclude mode for keys that carry mode state. */
function getFilterMode(
    filters: FiltersLike,
    key: ObjectFilterKey,
): FilterMode | undefined {
    if (isExtendedMetadataObjectFilterKey(key)) {
        const groupedValue = getExtendedMetadataGroupedValue(filters, key);
        if (!groupedValue) return undefined;
        return groupedValue.contains ? "include" : "exclude";
    }

    const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
    if (!modeCompanionKey) return undefined;

    if (modeCompanionKey === "containsDataLabels") {
        const modeValue = (filters as any)[modeCompanionKey];
        return modeValue ? "include" : "exclude";
    }

    const modeValue = (filters as any)[modeCompanionKey];
    return modeValue === false ? "exclude" : "include";
}

/** Returns source mode only when the source filter key is considered set. */
function getSourceMode(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
    sourceState: FilterSourceState,
): FilterMode | undefined {
    if (sourceState === "notSet") return undefined;
    return getFilterMode(sourceFilters, key);
}

/** Returns source-driven mode restrictions for a given filter key. */
function getModeRestrictionForSource(
    key: ObjectFilterKey,
    sourceState: FilterSourceState,
    sourceMode?: FilterMode,
): FilterModeRestriction | undefined {
    if (isExtendedMetadataObjectFilterKey(key)) {
        if (sourceState === "notSet" || !sourceMode) return undefined;
        if (sourceMode === "include") {
            return { allowedModes: ["include"] };
        }
        return { allowedModes: ["include", "exclude"] };
    }

    const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
    if (!modeCompanionKey || sourceState === "notSet") return undefined;

    if (sourceMode === "include") {
        return { allowedModes: ["include"] };
    }

    return { allowedModes: ["include", "exclude"] };
}

/** Returns source-driven allow/deny option restrictions for a filter key. */
function getOptionRestrictionForSource(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
    sourceState: FilterSourceState,
    sourceMode?: FilterMode,
    context?: FilterComparisonContext,
): FilterOptionRestriction | undefined {
    if (key === "agentIds") {
        const agentRestriction = getAgentRestrictionForSource(
            sourceFilters,
            sourceState,
            context,
        );
        if (agentRestriction) return agentRestriction;
    }

    if (sourceState === "notSet") return undefined;

    if (key === "safetyEvent") {
        return getSafetyEventRestrictionForSource(sourceFilters, sourceState);
    }

    if (key === "callDuration") {
        return undefined;
    }

    if (key === "messageCount") {
        return undefined;
    }

    // A chart may name any module, not only the ones its dashboard names. Module score conditions
    // are ANDed into the request rather than replacing the dashboard's, so a module the chart adds
    // narrows the population further and cannot widen it.
    if (key === "moduleScores") {
        return undefined;
    }

    if (isExtendedMetadataObjectFilterKey(key) && !sourceMode) {
        return undefined;
    }

    const sourceValues = getAllowedValuesForSource(sourceFilters, key);
    const modeCompanionKey = getModeCompanionKeyForFilterKey(key);

    if (isExtendedMetadataObjectFilterKey(key) && sourceMode === "exclude") {
        return {
            type: "denyList",
            values: sourceValues,
        };
    }

    if (modeCompanionKey && sourceMode === "exclude") {
        return {
            type: "denyList",
            values: sourceValues,
        };
    }

    return {
        type: "allowOnly",
        values: sourceValues,
    };
}

function getAgentRestrictionForSource(
    sourceFilters: FiltersLike,
    sourceState: FilterSourceState,
    context?: FilterComparisonContext,
): FilterOptionRestriction | undefined {
    const sourceAgentIds = getAllowedValuesForSource(sourceFilters, "agentIds");
    const sourceManagerIds = getAllowedValuesForSource(
        sourceFilters,
        "agentManagerIds",
    ).map((managerId) => `${managerId}`);

    if (sourceManagerIds.length === 0) {
        return undefined;
    }

    const agents = context?.agents ?? [];
    if (agents.length === 0) {
        return sourceState === "notSet"
            ? undefined
            : { type: "allowOnly", values: sourceAgentIds };
    }

    const managerIdSet = new Set(sourceManagerIds);
    const allowedAgentIds = new Map<string, unknown>();

    sourceAgentIds.forEach((agentId) => {
        allowedAgentIds.set(`${agentId}`, agentId);
    });

    agents.forEach((agent) => {
        const agentId = `${agent.id ?? ""}`;
        const managerId = `${agent.managerId ?? ""}`;
        if (!agentId || !managerIdSet.has(managerId)) return;
        allowedAgentIds.set(agentId, agent.id);
    });

    return {
        type: "allowOnly",
        values: Array.from(allowedAgentIds.values()),
    };
}

/** Checks whether a candidate mode is allowed by current mode restrictions. */
function isModeAllowed(
    modeRestriction: FilterModeRestriction | undefined,
    candidateMode: FilterMode | undefined,
): boolean {
    if (!modeRestriction) return true;
    const normalizedCandidateMode = candidateMode ?? "include";
    return modeRestriction.allowedModes.includes(normalizedCandidateMode);
}

/** Normalizes a candidate mode to a valid allowed mode when necessary. */
function getClampedCandidateMode(
    modeRestriction: FilterModeRestriction | undefined,
    candidateMode: FilterMode | undefined,
): FilterMode | undefined {
    if (!modeRestriction) return candidateMode;
    const normalizedCandidateMode = candidateMode ?? "include";

    if (modeRestriction.allowedModes.includes(normalizedCandidateMode)) {
        return normalizedCandidateMode;
    }

    return modeRestriction.allowedModes[0];
}

/** Collection Restriction Helpers **/

/** Applies primitive allow/deny restrictions to array values. */
function filterPrimitiveArrayByRestriction(
    values: unknown[],
    optionRestriction: FilterOptionRestriction | undefined,
): unknown[] {
    if (!optionRestriction) return values;

    const restrictedValues = new Set(
        optionRestriction.values.map((value) => `${value}`),
    );

    if (optionRestriction.type === "allowOnly") {
        return values.filter((entry) => restrictedValues.has(`${entry}`));
    }

    return values.filter((entry) => !restrictedValues.has(`${entry}`));
}

/** Unions primitive arrays using string identity while preserving value objects. */
function unionPrimitiveArraysByIdentity(
    left: unknown[],
    right: unknown[],
): unknown[] {
    const valueMap = new Map<string, unknown>();
    left.forEach((entry) => {
        valueMap.set(`${entry}`, entry);
    });
    right.forEach((entry) => {
        valueMap.set(`${entry}`, entry);
    });
    return Array.from(valueMap.values());
}

/** Applies object allow/deny restrictions based on a caller-provided identity. */
function filterObjectArrayByIdentity<T>(
    values: T[],
    optionRestriction: FilterOptionRestriction | undefined,
    identityBuilder: (entry: T) => string,
): T[] {
    if (!optionRestriction) return values;

    const restrictedValues = new Set(
        optionRestriction.values.map((value) => `${value}`),
    );
    if (optionRestriction.type === "allowOnly") {
        return values.filter((entry) =>
            restrictedValues.has(identityBuilder(entry)),
        );
    }

    return values.filter(
        (entry) => !restrictedValues.has(identityBuilder(entry)),
    );
}

/** Unions object arrays by identity and returns the de-duplicated values. */
function unionObjectArraysByIdentity<T>(
    left: T[],
    right: T[],
    identityBuilder: (entry: T) => string,
): T[] {
    const valueMap = new Map<string, T>();
    left.forEach((entry) => {
        const id = identityBuilder(entry);
        if (!id) return;
        valueMap.set(id, entry);
    });
    right.forEach((entry) => {
        const id = identityBuilder(entry);
        if (!id) return;
        valueMap.set(id, entry);
    });
    return Array.from(valueMap.values());
}

/** Model Results Helpers **/

/** Returns a stable identity for a question-result selection. */
function normalizeQuestionResult(
    value: unknown,
): QuestionResultFilter | undefined {
    if (!value || typeof value !== "object") return undefined;

    const licensedModuleId = `${(value as any).licensedModuleId ?? ""}`.trim();
    const questionId = `${(value as any).questionId ?? ""}`.trim();
    const tagIds = Array.isArray((value as any).tagIds)
        ? (value as any).tagIds
              .map((tagId: unknown) => `${tagId ?? ""}`.trim())
              .filter((tagId: string) => tagId.length > 0)
              .sort((left: string, right: string) => left.localeCompare(right))
        : [];

    if (!licensedModuleId || !questionId || tagIds.length === 0) {
        return undefined;
    }

    return {
        licensedModuleId,
        questionId,
        tagIds,
    };
}

/** Builds classifier/result restriction map for model-results filtering. */
function buildModelResultsRestrictionMap(
    optionRestriction: FilterOptionRestriction,
): Map<string, Set<string>> {
    const valueMap = new Map<string, Set<string>>();

    optionRestriction.values.forEach((entry) => {
        if (typeof entry !== "string") return;
        const [classifierId, result] = entry.split("::");
        if (!classifierId || !result) return;
        if (!valueMap.has(classifierId)) {
            valueMap.set(classifierId, new Set<string>());
        }
        valueMap.get(classifierId)!.add(result);
    });

    return valueMap;
}

/** Applies model-result restrictions to classifier/result selections. */
function clampModelResultsByRestriction(
    candidateValue: unknown,
    optionRestriction: FilterOptionRestriction | undefined,
): SlimLLMClassifierResponse[] {
    if (!Array.isArray(candidateValue)) return [];
    if (!optionRestriction) return candidateValue as SlimLLMClassifierResponse[];

    const restrictedValues = buildModelResultsRestrictionMap(optionRestriction);

    return (candidateValue as SlimLLMClassifierResponse[])
        .map((model) => {
            const classifierId = `${model?.id ?? ""}`;
            const restrictedResults = restrictedValues.get(classifierId);

            if (optionRestriction.type === "allowOnly" && !restrictedResults) {
                return null;
            }

            const filteredResults = (model.results ?? []).filter((result) => {
                if (!restrictedResults) return true;
                if (optionRestriction.type === "allowOnly") {
                    return restrictedResults.has(result);
                }
                return !restrictedResults.has(result);
            });

            if (filteredResults.length === 0) return null;

            return {
                ...model,
                results: filteredResults,
            };
        })
        .filter((entry): entry is SlimLLMClassifierResponse =>
            Boolean(entry),
        );
}

/** Source Constraint Helpers **/

/** Determines whether object-level editing should be disabled for a key. */
function getDisabledAtObject(
    key: ObjectFilterKey,
    sourceState: FilterSourceState,
    sourceMode?: FilterMode,
): boolean {
    if (sourceState === "notSet") return false; // no source value? filter enabled

    if (key === "safetyEvent") {
        return sourceState === "singleValue";
    }

    if (key === "callDuration") {
        return false;
    }

    if (key === "messageCount") {
        return false;
    }

    if (key === "moduleScores") {
        return false;
    }

    if (isExtendedMetadataObjectFilterKey(key)) {
        // Non-groupable source-level metadata disables object-level editing.
        if (!sourceMode) return true;
        // Groupable metadata matches topics/data labels semantics:
        // exclude mode can still be narrowed even from a single selected value.
        if (sourceMode === "exclude") return false;
        return sourceState === "singleValue";
    }

    const rule = getRuleMetadata(key);
    if (
        rule.ruleType === "singleSelect" ||
        rule.ruleType === "sourceExclusive"
    ) {
        return true; // source value and singleSelect rule? filter disabled
    }

    if (rule.modeCompanionKey && sourceMode === "exclude") {
        return false; // source value but in DOES NOT CONTAIN mode? filter enabled
    }

    return sourceState === "singleValue"; // source value and only one value? filter disabled
    // source value and more than one value to choose from? filter enabled
}

/** Candidate Clamp Helpers **/

/**
 * Produces the candidate value/mode after applying source-driven object-filter constraints.
 *
 * This is the central clamp routine used by both:
 * - request-time safety enforcement (merged request filters)
 * - conflict-resolution flows (remove invalid object values)
 *
 * The function returns both:
 * - clamped values (`clampedCandidateValue`, `clampedCandidateMode`)
 * - the evaluated source policy (`optionRestriction`, `modeRestriction`, `sourceState`, `sourceMode`)
 */
function getClampedCandidateForKey(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
    candidateValue: unknown,
    candidateMode?: FilterMode,
    context?: FilterComparisonContext,
): {
    clampedCandidateValue: unknown;
    clampedCandidateMode?: FilterMode;
    optionRestriction?: FilterOptionRestriction;
    modeRestriction?: FilterModeRestriction;
    rangeRestriction?: FilterRangeRestriction;
    moduleScoreRestriction?: ModuleScoreRestriction;
    sourceState: FilterSourceState;
    sourceMode?: FilterMode;
} {
    // Evaluate source constraints once so all branches use the same policy.
    const sourceState = getSourceFilterState(sourceFilters, key);
    const sourceMode = getSourceMode(sourceFilters, key, sourceState);
    const optionRestriction = getOptionRestrictionForSource( // are options in a denyList or an allowList?
        sourceFilters,
        key,
        sourceState,
        sourceMode,
        context,
    );
    const modeRestriction = getModeRestrictionForSource( // are we allowed by the source to include only or can we do both?
        key,
        sourceState,
        sourceMode,
    );
    const sourceValue = getByKey(sourceFilters, key);
    const rangeRestriction =
        key === "callDuration"
            ? getCallDurationRestrictionForSource(sourceFilters)
            : key === "messageCount"
              ? getMessageCountRestrictionForSource(sourceFilters)
            : undefined;
    const moduleScoreRestriction =
        key === "moduleScores"
            ? getModuleScoreRestrictionForSource(sourceFilters)
            : undefined;
    const clampedCandidateMode = getClampedCandidateMode(
        modeRestriction,
        candidateMode,
    );

    if (key === "moduleScores") {
        if (isFilterValueEmpty(candidateValue)) {
            return {
                clampedCandidateValue: candidateValue,
                clampedCandidateMode,
                optionRestriction,
                modeRestriction,
                rangeRestriction,
                moduleScoreRestriction,
                sourceState,
                sourceMode,
            };
        }

        return {
            clampedCandidateValue: clampModuleScoresToSource(
                sourceFilters,
                candidateValue,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            moduleScoreRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (key === "rbcFilterItems") {
        return {
            clampedCandidateValue: normalizeRbcFilterItems(candidateValue),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (key === "outlineSegmentFilterItems") {
        return {
            clampedCandidateValue: normalizeOutlineFilterItems(candidateValue),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    // Nothing to clamp if source does not constrain this key, or candidate is empty.
    if (
        (sourceState === "notSet" && !optionRestriction) ||
        isFilterValueEmpty(candidateValue)
    ) {
        return {
            clampedCandidateValue: candidateValue,
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (key === "callDuration") {
        return {
            clampedCandidateValue: clampCallDurationToSourceRange(
                sourceFilters,
                candidateValue,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (key === "messageCount") {
        return {
            clampedCandidateValue: clampMessageCountToSourceRange(
                sourceFilters,
                candidateValue,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (isExtendedMetadataObjectFilterKey(key)) {
        // Non-groupable source-level metadata should fully disable object-level metadata filters.
        if (!sourceMode) {
            return {
                clampedCandidateValue: undefined,
                clampedCandidateMode: undefined,
                optionRestriction,
                modeRestriction,
                sourceState,
                sourceMode,
            };
        }

        const candidateValues = Array.isArray(candidateValue)
            ? (candidateValue as unknown[])
            : [];

        if (
            optionRestriction?.type === "denyList" &&
            sourceMode === "exclude" &&
            clampedCandidateMode === "exclude"
        ) {
            const sourceValues = Array.isArray(getByKey(sourceFilters, key))
                ? ((getByKey(sourceFilters, key) as unknown[]) ?? [])
                : [];
            return {
                clampedCandidateValue: unionPrimitiveArraysByIdentity(
                    candidateValues,
                    sourceValues,
                ),
                clampedCandidateMode,
                optionRestriction,
                modeRestriction,
                rangeRestriction,
                sourceState,
                sourceMode,
            };
        }

        return {
            clampedCandidateValue: filterPrimitiveArrayByRestriction(
                candidateValues,
                optionRestriction,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    // modelResults is a custom nested structure: clamp classifier/result pairs.
    if (key === "modelResults") {
        return {
            clampedCandidateValue: clampModelResultsByRestriction(
                candidateValue,
                optionRestriction,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    if (key === "safetyEvent") {
        return {
            clampedCandidateValue: clampSafetyEventToSource(
                sourceValue,
                candidateValue,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    // topics/themes/primaryTopics/drivers/languages/dataLabels are arrays of objects; clamp by identity fields.
    if (
        key === "topics" ||
        key === "themes" ||
        key === "primaryTopics" ||
        key === "drivers" ||
        key === "languages" ||
        key === "dataLabels"
    ) {
        // Non-array candidate is treated as empty selection.
        if (!Array.isArray(candidateValue)) {
            return {
                clampedCandidateValue: [],
                clampedCandidateMode,
                optionRestriction,
                modeRestriction,
                sourceState,
                sourceMode,
            };
        }

        const idBuilder = ARRAY_OBJECT_KEY_ID[key];
        const candidateValues = candidateValue as any[];

        // Source exclude + candidate exclude means we preserve/merge excluded set.
        // This keeps "does not contain" semantics narrowing correctly.
        if (
            optionRestriction?.type === "denyList" &&
            sourceMode === "exclude" &&
            clampedCandidateMode === "exclude"
        ) {
            const sourceValues = Array.isArray(getByKey(sourceFilters, key))
                ? (getByKey(sourceFilters, key) as any[])
                : [];
            const merged = unionObjectArraysByIdentity(
                candidateValues,
                sourceValues,
                idBuilder,
            );

            return {
                clampedCandidateValue: merged,
                clampedCandidateMode,
                optionRestriction,
                modeRestriction,
                sourceState,
                sourceMode,
            };
        }

        // Default object-array behavior: apply allow-only/deny-list by identity.
        const filtered = filterObjectArrayByIdentity(
            candidateValues,
            optionRestriction,
            idBuilder,
        );
        return {
            clampedCandidateValue: filtered,
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    // Generic array branch for primitive array keys.
    if (Array.isArray(candidateValue)) {
        const candidateValues = candidateValue as unknown[];
        // Source exclude + candidate exclude => merge excluded values to preserve semantics.
        if (
            optionRestriction?.type === "denyList" &&
            sourceMode === "exclude" &&
            clampedCandidateMode === "exclude"
        ) {
            const sourceValues = Array.isArray(getByKey(sourceFilters, key))
                ? ((getByKey(sourceFilters, key) as unknown[]) ?? [])
                : [];
            return {
                clampedCandidateValue: unionPrimitiveArraysByIdentity(
                    candidateValues,
                    sourceValues,
                ),
                clampedCandidateMode,
                optionRestriction,
                modeRestriction,
                sourceState,
                sourceMode,
            };
        }

        // Default primitive-array behavior: allowOnly or denyList filtering.
        return {
            clampedCandidateValue: filterPrimitiveArrayByRestriction(
                candidateValues,
                optionRestriction,
            ),
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    // Scalar fallback when no source restriction exists.
    if (!optionRestriction) {
        return {
            clampedCandidateValue: candidateValue,
            clampedCandidateMode,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            sourceState,
            sourceMode,
        };
    }

    const restrictedValues = new Set(
        optionRestriction.values.map((value) => `${value}`),
    );
    const candidateString = `${candidateValue}`;

    // Scalar allowOnly/denyList evaluation.
    const isAllowed =
        optionRestriction.type === "allowOnly"
            ? restrictedValues.has(candidateString)
            : !restrictedValues.has(candidateString);

    return {
        clampedCandidateValue: isAllowed ? candidateValue : undefined,
        clampedCandidateMode,
        optionRestriction,
        modeRestriction,
        rangeRestriction,
        sourceState,
        sourceMode,
    };
}

/** Conflict Detection Helpers **/

/** Returns normalized candidate values that violate option restrictions. */
function getConflictingValuesFromRestriction(
    key: ObjectFilterKey,
    candidateValue: unknown,
    optionRestriction: FilterOptionRestriction | undefined,
    candidateMode?: FilterMode,
    sourceMode?: FilterMode,
): unknown[] {
    if (!optionRestriction || isFilterValueEmpty(candidateValue)) {
        return [];
    }

    if (key === "safetyEvent") {
        const sourceValue = optionRestriction.values[0];
        return areSafetyEventsEqual(
            clampSafetyEventToSource(sourceValue, candidateValue),
            candidateValue,
        )
            ? []
            : getNormalizedValuesForKey(key, candidateValue);
    }

    if (
        optionRestriction.type === "denyList" &&
        sourceMode === "exclude" &&
        candidateMode === "exclude"
    ) {
        return [];
    }

    const candidateValues = getNormalizedValuesForKey(key, candidateValue);
    const restrictedValues = new Set(
        optionRestriction.values.map((value) => `${value}`),
    );

    if (optionRestriction.type === "allowOnly") {
        return candidateValues.filter((value) => !restrictedValues.has(value));
    }

    return candidateValues.filter((value) => restrictedValues.has(value));
}

/** Builds a human-readable conflict reason for a key comparison result. */
function getConflictReason({
    key,
    sourceExclusiveConflict,
    modeConflict,
    nonGroupableMetadataConflict,
    callDurationConflict,
    messageCountConflict,
    moduleScoreConflict,
    safetyEventConflict,
}: {
    key: ObjectFilterKey;
    sourceExclusiveConflict: boolean;
    modeConflict: boolean;
    nonGroupableMetadataConflict: boolean;
    callDurationConflict: boolean;
    messageCountConflict: boolean;
    moduleScoreConflict: boolean;
    safetyEventConflict: boolean;
}): string {
    if (sourceExclusiveConflict) {
        return `Object filter cannot be combined with a source selection for ${key}.`;
    }

    if (modeConflict) {
        return `Object filter mode is incompatible with source selection for ${key}.`;
    }

    if (nonGroupableMetadataConflict) {
        return `Object filter is incompatible with source-level non-groupable metadata for ${key}.`;
    }

    if (callDurationConflict) {
        return `Object filter duration is outside source selection for ${key}.`;
    }

    if (messageCountConflict) {
        return `Object filter message count is outside source selection for ${key}.`;
    }

    if (moduleScoreConflict) {
        return `Object filter module score is outside source selection for ${key}.`;
    }

    if (safetyEventConflict) {
        return `Object filter contains safety-event values outside the source selection for ${key}.`;
    }

    return `Object filter contains values outside source selection for ${key}.`;
}

/** Filter Value Access Helpers **/

/**
 * Returns true when a filter value should be treated as unset.
 * Empty arrays are considered unset to preserve source-level inheritance semantics.
 */
export function isFilterValueEmpty(value: unknown): boolean {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) {
        if (value.length === 0) return true;
        if (
            value.every(
                (entry) =>
                    !!entry &&
                    typeof entry === "object" &&
                    Array.isArray((entry as any).classifierIds),
            )
        ) {
            return value.every(
                (entry) => ((entry as any).classifierIds ?? []).length === 0,
            );
        }
        if (
            value.every(
                (entry) =>
                    !!entry &&
                    typeof entry === "object" &&
                    Array.isArray((entry as any).segments),
            )
        ) {
            return value.every(
                (entry) => ((entry as any).segments ?? []).length === 0,
            );
        }
        return false;
    }
    return false;
}

/**
 * Reads a filter value by key from either a plain filters object or an instance store.
 */
function getByKey(filters: FiltersLike, key: ObjectFilterKey): unknown {
    if (key === "callDuration") {
        return getCallDurationTupleFromFilters(filters);
    }

    if (key === "messageCount") {
        return getMessageCountTupleFromFilters(filters);
    }

    if (isExtendedMetadataObjectFilterKey(key)) {
        const groupedValue = getExtendedMetadataGroupedValue(filters, key);
        if (groupedValue) return groupedValue.values;
        return getExtendedMetadataStringValue(filters, key);
    }

    return (filters as any)[key];
}

/** Writes a key-specific value back into an `ApplicationFilters` object. */
function setByKey(
    filters: Partial<ApplicationFilters>,
    key: ObjectFilterKey,
    value: unknown,
    mode?: FilterMode,
) {
    if (key === "callDuration") {
        const callDurationRange = getCallDurationRangeFromTuple(value);
        if (!callDurationRange.isSet) {
            filters.minCallDuration = undefined;
            filters.maxCallDuration = undefined;
            return;
        }

        filters.minCallDuration =
            callDurationRange.min > 0 ? callDurationRange.min : undefined;
        filters.maxCallDuration =
            callDurationRange.max > 0 ? callDurationRange.max : undefined;
        return;
    }

    if (key === "messageCount") {
        const messageCountRange = getMessageCountRangeFromTuple(value);
        if (!messageCountRange.isSet) {
            filters.minMessageCount = undefined;
            filters.maxMessageCount = undefined;
            return;
        }

        filters.minMessageCount =
            messageCountRange.min > 0 ? messageCountRange.min : undefined;
        filters.maxMessageCount =
            messageCountRange.max > 0 ? messageCountRange.max : undefined;
        return;
    }

    if (!isExtendedMetadataObjectFilterKey(key)) {
        (filters as any)[key] = value;
        return;
    }

    const metadataKey = getExtendedMetadataKey(key);
    if (!metadataKey) return;

    const nextExtendedMetadata = {
        ...(filters.extendedMetadata ?? {}),
    };
    const nextExtendedMetadataGrouped = {
        ...(filters.extendedMetadataGrouped ?? {}),
    };

    if (Array.isArray(value) && value.length > 0 && mode) {
        const stringValues = value
            .map((entry) => `${entry}`.trim())
            .filter((entry) => entry.length > 0);

        if (stringValues.length > 0) {
            nextExtendedMetadataGrouped[metadataKey] = {
                contains: mode === "include",
                values: stringValues,
            };

            if (mode === "include") {
                nextExtendedMetadata[metadataKey] = stringValues.join(", ");
            } else {
                delete nextExtendedMetadata[metadataKey];
            }
        } else {
            delete nextExtendedMetadataGrouped[metadataKey];
            delete nextExtendedMetadata[metadataKey];
        }
    } else if (typeof value === "string" && value.trim().length > 0) {
        delete nextExtendedMetadataGrouped[metadataKey];
        nextExtendedMetadata[metadataKey] = value;
    } else {
        delete nextExtendedMetadataGrouped[metadataKey];
        delete nextExtendedMetadata[metadataKey];
    }

    filters.extendedMetadata =
        Object.keys(nextExtendedMetadata).length > 0
            ? nextExtendedMetadata
            : undefined;
    filters.extendedMetadataGrouped =
        Object.keys(nextExtendedMetadataGrouped).length > 0
            ? nextExtendedMetadataGrouped
            : undefined;
}

/** Filter Value Normalization Helpers **/

/**
 * Flattens modelResults into normalized classifier/result pairs (`classifierId::result`)
 * so comparisons and set operations can be done with primitive values.
 */
function getModelResultPairs(value: unknown): string[] {
    if (!Array.isArray(value)) return [];

    const pairs: string[] = [];
    (value as SlimLLMClassifierResponse[]).forEach((model) => {
        const modelId = `${model?.id ?? ""}`;
        if (!modelId) return;
        (model?.results ?? []).forEach((result) => {
            pairs.push(`${modelId}::${result}`);
        });
    });

    return pairs;
}

/**
 * Normalizes any supported object-filter key into string values used for
 * equality checks, subset checks, and conflict detection.
 */
function getNormalizedValuesForKey(
    key: ObjectFilterKey,
    value: unknown,
): string[] {
    if (isFilterValueEmpty(value)) return [];

    if (key === "callDuration") {
        const range = getCallDurationRangeFromTuple(value);
        if (!range.isSet) return [];
        const min = range.min > 0 ? range.min : 0;
        const max = range.max > 0 ? range.max : CALL_DURATION_MAX;
        return [`${min}..${max}`];
    }

    if (key === "messageCount") {
        const range = getMessageCountRangeFromTuple(value);
        if (!range.isSet) return [];
        const min = range.min > 0 ? range.min : 0;
        const max = range.max > 0 ? range.max : MESSAGE_COUNT_MAX;
        return [`${min}..${max}`];
    }

    if (key === "rbcFilterItems") {
        return getRbcClassifierIds(value);
    }

    if (key === "outlineSegmentFilterItems") {
        return getOutlineSegmentIds(value);
    }

    if (key === "modelResults") {
        return getModelResultPairs(value);
    }

    if (key === "moduleScores") {
        return normalizeModuleScores(value).map(
            (moduleScore) => moduleScore.licensedModuleId,
        );
    }

    if (key === "questionResults") {
        if (!Array.isArray(value)) return [];

        return value
            .map((entry) => normalizeQuestionResult(entry))
            .filter(
                (entry): entry is QuestionResultFilter => entry !== undefined,
            )
            .map((entry) =>
                JSON.stringify({
                    licensedModuleId: entry.licensedModuleId,
                    questionId: entry.questionId,
                    tagIds: entry.tagIds,
                }),
            );
    }

    if (
        key === "topics" ||
        key === "themes" ||
        key === "primaryTopics" ||
        key === "drivers" ||
        key === "languages" ||
        key === "dataLabels"
    ) {
        if (!Array.isArray(value)) return [];
        const idBuilder = ARRAY_OBJECT_KEY_ID[key];
        return value
            .map((entry) => idBuilder(entry))
            .filter((entry) => entry.length > 0);
    }

    if (Array.isArray(value)) {
        return value.map((entry) => `${entry}`);
    }

    if (key === "safetyEvent") {
        const normalizedValue = normalizeSafetyEvent(value);
        if (!normalizedValue) return [];

        return [
            JSON.stringify({
                identification: normalizedValue.identificationKey,
                typeOperator: normalizedValue.typeOperator,
                types: normalizedValue.typeKeys,
                acknowledgments: normalizedValue.acknowledgmentKeys,
            }),
        ];
    }

    return [`${value}`];
}

/**
 * Returns the number of distinct normalized values for a key.
 */
function countDistinctValues(key: ObjectFilterKey, value: unknown): number {
    return new Set(getNormalizedValuesForKey(key, value)).size;
}

/** Source State Helpers **/

/**
 * Determines whether the source filter for a key is unset, constrained to one value,
 * or constrained to multiple values.
 */
export function getSourceFilterState(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
): FilterSourceState {
    const sourceValue = getByKey(sourceFilters, key);

    if (key === "callDuration") {
        const range = getCallDurationRangeFromTuple(sourceValue);
        return range.isSet ? "multiValue" : "notSet";
    }

    if (key === "messageCount") {
        const range = getMessageCountRangeFromTuple(sourceValue);
        return range.isSet ? "multiValue" : "notSet";
    }

    if (key === "rbcFilterItems") {
        const classifierCount = new Set([
            ...getRbcClassifierIds(sourceValue, "include"),
            ...getRbcClassifierIds(sourceValue, "exclude"),
        ]).size;

        if (classifierCount === 0) return "notSet";
        if (classifierCount === 1) return "singleValue";
        return "multiValue";
    }

    if (key === "outlineSegmentFilterItems") {
        const segmentCount = new Set([
            ...getOutlineSegmentIds(sourceValue, "include"),
            ...getOutlineSegmentIds(sourceValue, "exclude"),
        ]).size;

        if (segmentCount === 0) return "notSet";
        if (segmentCount === 1) return "singleValue";
        return "multiValue";
    }

    if (key === "safetyEvent") {
        const normalizedSource = normalizeSafetyEvent(sourceValue);
        if (!normalizedSource) return "notSet";
        if (normalizedSource.identification === null) {
            return normalizedSource.typeKeys.length +
                normalizedSource.acknowledgmentKeys.length === 1
                ? "singleValue"
                : "multiValue";
        }
        if (!isSafetyEventIdentified(normalizedSource.identification)) {
            return "singleValue";
        }

        return isSingleValuedIdentifiedSafetyEvent(normalizedSource)
            ? "singleValue"
            : "multiValue";
    }

    const distinctCount = countDistinctValues(key, sourceValue);
    if (distinctCount === 0) return "notSet";
    if (distinctCount === 1) return "singleValue";
    return "multiValue";
}

/**
 * Returns the normalized allowed value set implied by the source filters for a key.
 * For unset source state, returns an empty array.
 */
export function getAllowedValuesForSource(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
): unknown[] {
    const sourceState = getSourceFilterState(sourceFilters, key);
    if (sourceState === "notSet") return [];

    if (key === "callDuration") {
        const sourceValue = getByKey(sourceFilters, key);
        const sourceRange = getCallDurationRangeFromTuple(sourceValue);
        const min = sourceRange.min > 0 ? sourceRange.min : 0;
        const max = sourceRange.max > 0 ? sourceRange.max : CALL_DURATION_MAX;
        return [min, max];
    }

    if (key === "messageCount") {
        const sourceValue = getByKey(sourceFilters, key);
        const sourceRange = getMessageCountRangeFromTuple(sourceValue);
        const min = sourceRange.min > 0 ? sourceRange.min : 0;
        const max = sourceRange.max > 0 ? sourceRange.max : MESSAGE_COUNT_MAX;
        return [min, max];
    }

    if (key === "rbcFilterItems") {
        return Array.from(
            new Set([
                ...getRbcClassifierIds(getByKey(sourceFilters, key), "include"),
                ...getRbcClassifierIds(getByKey(sourceFilters, key), "exclude"),
            ]),
        );
    }

    if (key === "outlineSegmentFilterItems") {
        return Array.from(
            new Set([
                ...getOutlineSegmentIds(getByKey(sourceFilters, key), "include"),
                ...getOutlineSegmentIds(getByKey(sourceFilters, key), "exclude"),
            ]),
        );
    }

    if (key === "modelResults") {
        return getModelResultPairs(getByKey(sourceFilters, key));
    }

    if (key === "moduleScores") {
        return normalizeModuleScores(getByKey(sourceFilters, key)).map(
            (moduleScore) => moduleScore.licensedModuleId,
        );
    }

    return getNormalizedValuesForKey(key, getByKey(sourceFilters, key));
}

/** Public Comparison API **/

/**
 * Compares source filters vs candidate filters per key and returns
 * a structured result containing source state, conflicts, and allowed values.
 */
export function compareFiltersByKey(
    sourceFilters: FiltersLike,
    candidateFilters: FiltersLike,
    keys: readonly ObjectFilterKey[] = OBJECT_FILTER_KEYS,
    context?: FilterComparisonContext,
): FiltersComparisonResult {
    const byKey = {} as Record<string, FilterComparison>;
    const conflicts: FilterComparison[] = [];

    keys.forEach((key) => {
        const sourceState = getSourceFilterState(sourceFilters, key); // notSet, singleValue, multiValue
        const sourceIsSet = sourceState !== "notSet";
        const sourceValue = getByKey(sourceFilters, key);
        const sourceMode = getSourceMode(sourceFilters, key, sourceState); // Whether it's include or exclude
        const candidateValue = getByKey(candidateFilters, key);
        const candidateMode = getFilterMode(candidateFilters, key); // Include exclude
        const {
            optionRestriction, // allowList or denyList and the values from source
            modeRestriction, // what modes are allowed by source
            rangeRestriction,
            moduleScoreRestriction,
            clampedCandidateValue,
            clampedCandidateMode,
        } = getClampedCandidateForKey(
            sourceFilters,
            key,
            candidateValue,
            candidateMode,
            context,
        );
        const disabledAtObject =
            key === "agentIds" &&
            optionRestriction?.type === "allowOnly" &&
            optionRestriction.values.length > 1
                ? false
                : getDisabledAtObject(key, sourceState, sourceMode);
        const rule = getRuleMetadata(key);
        const restrictionConflictingValues = getConflictingValuesFromRestriction(
            key,
            candidateValue,
            optionRestriction,
            candidateMode,
            sourceMode,
        );
        const sourceExclusiveConflict =
            rule.ruleType === "sourceExclusive" &&
            sourceIsSet &&
            !isFilterValueEmpty(candidateValue);
        const sourceExclusiveConflictingValues = sourceExclusiveConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const modeConflict =
            sourceIsSet &&
            !isFilterValueEmpty(candidateValue) &&
            !isModeAllowed(modeRestriction, candidateMode);
        const modeConflictingValues = modeConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const nonGroupableMetadataConflict =
            isExtendedMetadataObjectFilterKey(key) &&
            sourceIsSet &&
            !sourceMode &&
            !isFilterValueEmpty(candidateValue);
        const nonGroupableMetadataConflictingValues =
            nonGroupableMetadataConflict
                ? getNormalizedValuesForKey(key, candidateValue)
                : [];
        const callDurationConflict =
            key === "callDuration" &&
            !isFilterValueEmpty(candidateValue) &&
            JSON.stringify(candidateValue) !==
                JSON.stringify(clampedCandidateValue);
        const callDurationConflictingValues = callDurationConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const messageCountConflict =
            key === "messageCount" &&
            !isFilterValueEmpty(candidateValue) &&
            JSON.stringify(candidateValue) !==
                JSON.stringify(clampedCandidateValue);
        const messageCountConflictingValues = messageCountConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const moduleScoreConflict =
            key === "moduleScores" &&
            !isFilterValueEmpty(candidateValue) &&
            !areModuleScoresEqual(candidateValue, clampedCandidateValue);
        const moduleScoreConflictingValues = moduleScoreConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const safetyEventConflict =
            key === "safetyEvent" &&
            !isFilterValueEmpty(candidateValue) &&
            !areSafetyEventsEqual(candidateValue, clampedCandidateValue);
        const safetyEventConflictingValues = safetyEventConflict
            ? getNormalizedValuesForKey(key, candidateValue)
            : [];
        const conflictingValues = Array.from(
            new Set([
                ...sourceExclusiveConflictingValues,
                ...modeConflictingValues,
                ...nonGroupableMetadataConflictingValues,
                ...callDurationConflictingValues,
                ...messageCountConflictingValues,
                ...moduleScoreConflictingValues,
                ...safetyEventConflictingValues,
                ...restrictionConflictingValues.map((value) => `${value}`),
            ]),
        );
        const conflict =
            sourceExclusiveConflict ||
            modeConflict ||
            nonGroupableMetadataConflict ||
            callDurationConflict ||
            messageCountConflict ||
            moduleScoreConflict ||
            safetyEventConflict ||
            conflictingValues.length > 0;

        const comparison: FilterComparison = {
            key,
            sourceState,
            sourceIsSet,
            sourceValue,
            sourceMode,
            candidateValue,
            candidateMode,
            clampedCandidateValue,
            clampedCandidateMode,
            disabledAtObject,
            optionRestriction,
            modeRestriction,
            rangeRestriction,
            moduleScoreRestriction,
            conflictRule: getConflictRuleForFilterKey(key),
            conflict,
            allowedValues:
                sourceState === "multiValue"
                    ? getAllowedValuesForSource(sourceFilters, key)
                    : undefined,
            conflictingValues: conflict ? conflictingValues : undefined,
            reason: conflict
                ? getConflictReason({
                      key,
                      sourceExclusiveConflict,
                      modeConflict,
                      nonGroupableMetadataConflict,
                      callDurationConflict,
                      messageCountConflict,
                      moduleScoreConflict,
                      safetyEventConflict,
                  })
                : undefined,
        };

        byKey[key] = comparison;

        if (conflict) {
            conflicts.push(comparison);
        }
    });

    return { byKey, conflicts };
}

/**
 * Returns normalized candidate values that are not allowed by the source filter state.
 */
export function getConflictingValuesForKey(
    sourceFilters: FiltersLike,
    candidateFilters: FiltersLike,
    key: ObjectFilterKey,
    context?: FilterComparisonContext,
): unknown[] {
    return (
        compareFiltersByKey(sourceFilters, candidateFilters, [key], context)
            .byKey[key].conflictingValues ?? []
    );
}

/** Public Clamp API **/

/**
 * Clamps a single candidate object-filter value to what is allowed by source filters.
 * Returns the original candidate when source is unset or candidate is empty.
 */
export function clampObjectValueForKey(
    sourceFilters: FiltersLike,
    key: ObjectFilterKey,
    candidateValue: unknown,
    candidateMode?: FilterMode,
    context?: FilterComparisonContext,
): unknown {
    return getClampedCandidateForKey(
        sourceFilters,
        key,
        candidateValue,
        candidateMode,
        context,
    ).clampedCandidateValue;
}

/**
 * Clamps all supported object-filter keys in candidate filters against source filters.
 */
export function clampObjectFiltersToSource(
    sourceFilters: FiltersLike,
    candidateFilters: FiltersLike,
    keys: readonly ObjectFilterKey[] = OBJECT_FILTER_KEYS,
    context?: FilterComparisonContext,
): Partial<ApplicationFilters> {
    const next: Partial<ApplicationFilters> = {};

    keys.forEach((key) => {
        const currentValue = getByKey(candidateFilters, key);
        const currentMode = getFilterMode(candidateFilters, key);
        const {
            clampedCandidateValue,
            clampedCandidateMode,
            sourceState,
        } = getClampedCandidateForKey(
            sourceFilters,
            key,
            currentValue,
            currentMode,
            context,
        );
        setByKey(
            next,
            key,
            clampedCandidateValue,
            clampedCandidateMode,
        );

        const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
        if (
            modeCompanionKey &&
            clampedCandidateMode &&
            !isFilterValueEmpty(clampedCandidateValue)
        ) {
            (next as any)[modeCompanionKey] =
                clampedCandidateMode === "include";
        }

        if (key === "hierarchyIds") {
            if (isFilterValueEmpty(clampedCandidateValue)) {
                next.selectedCategories = [];
                next.selectedSubcategories = [];
                return;
            }

            if (sourceState === "notSet") {
                next.selectedCategories = [
                    ...(((candidateFilters as any).selectedCategories ??
                        []) as string[]),
                ];
                next.selectedSubcategories = [
                    ...(((candidateFilters as any).selectedSubcategories ??
                        []) as string[]),
                ];
                return;
            }

            // Companion category filters can't be safely intersected by hierarchy ID.
            // Clear them when source-level hierarchy restrictions are active.
            next.selectedCategories = [];
            next.selectedSubcategories = [];
        }
    });

    return next;
}
